import { useEffect, useRef } from "react";

// Custom Google OAuth — Google Identity Services (GSI).
// Carica lo script di Google una sola volta (idempotente) e mostra il
// bottone ufficiale "Sign in with Google" in italiano. Quando l'utente
// completa il flow, GSI restituisce un ID token JWT che inoltriamo al
// backend (`POST /api/auth/google/custom`) per la verifica e creazione
// sessione.
//
// Vantaggi rispetto a Emergent-managed Google Auth:
//   - Zero step intermedi: redirect diretto Google ↔ nostra app.
//   - Branding nostro nella schermata di consenso (gestita da te in
//     Google Cloud Console).
//   - Funziona anche in iframe / GHL widgets.
//
// L'utente DEVE già esistere in DB con email coerente, altrimenti il
// backend ritorna 403.

const GSI_SCRIPT_SRC = "https://accounts.google.com/gsi/client";

function loadGsi() {
  // Idempotente: se lo script è già nel DOM (caricato da un altro
  // componente, es. login screen montato 2 volte), restituiamo subito.
  if (window.google?.accounts?.id) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${GSI_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.src = GSI_SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Impossibile caricare Google Identity Services"));
    document.head.appendChild(s);
  });
}

export default function GoogleSignInButton({ onCredential, onError, disabled = false }) {
  const containerRef = useRef(null);
  const clientId = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID;

  useEffect(() => {
    if (!clientId) {
      console.warn("REACT_APP_GOOGLE_OAUTH_CLIENT_ID non impostato — bottone Google nascosto");
      return;
    }
    let cancelled = false;
    loadGsi()
      .then(() => {
        if (cancelled || !window.google?.accounts?.id || !containerRef.current) return;
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (resp) => {
            // resp.credential è il JWT ID token firmato da Google.
            if (resp && resp.credential) {
              onCredential?.(resp.credential);
            } else if (onError) {
              onError(new Error("Nessuna credenziale ricevuta da Google"));
            }
          },
          // UX: One Tap automatico. Se l'utente è già loggato in Google
          // nel browser, GSI riconosce l'account e propone il login
          // diretto SENZA aprire alcun popup (mostra solo un piccolo
          // banner in alto a destra che l'utente clicca per confermare).
          // `auto_select` accede automaticamente se l'utente ha già
          // dato consenso in passato. `itp_support` migliora la
          // compatibilità con Safari ITP (Intelligent Tracking Prevention).
          auto_select: true,
          itp_support: true,
          cancel_on_tap_outside: false,
          context: "signin",
          ux_mode: "popup",
        });
        // Renderizza il bottone ufficiale (sempre disponibile come fallback).
        window.google.accounts.id.renderButton(containerRef.current, {
          theme: "outline",
          size: "large",
          type: "standard",
          shape: "rectangular",
          text: "continue_with",
          logo_alignment: "left",
          locale: "it",
          width: containerRef.current.offsetWidth || 320,
        });
        // Attiva One Tap (top-right banner). Se il browser/Google non
        // possono mostrarlo (es. utente non loggato, blocco terze parti)
        // viene silenziosamente saltato e l'utente userà il bottone.
        try {
          window.google.accounts.id.prompt();
        } catch {
          // Ignora: il bottone è sempre disponibile come fallback.
        }
      })
      .catch((e) => {
        console.error("GSI load error:", e);
        onError?.(e);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  if (!clientId) return null;
  return (
    <div className="w-full" data-testid="google-signin-container">
      <div
        ref={containerRef}
        className={`w-full ${disabled ? "opacity-50 pointer-events-none" : ""}`}
      />
    </div>
  );
}
